import React, { FC, useMemo } from 'react'

import { alpha, PaletteColorOptions } from '@mui/material'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { ColorPartial } from '@mui/material/styles/createPalette'

import { useThemeMode } from './theme/ThemeModeContext'

declare module '@mui/material/styles' {
  interface PaletteOptions {
    green?: PaletteColorOptions
    red?: PaletteColorOptions
    blue: PaletteColorOptions
    cyan: PaletteColorOptions
    brown: PaletteColorOptions
    yellow: PaletteColorOptions
    black: PaletteColorOptions
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    green: ColorPartial
    red: ColorPartial
    blue: ColorPartial
    cyan: ColorPartial
    brown: ColorPartial
    yellow: ColorPartial
    black: ColorPartial
  }
}

function pxToRem(value: number) {
  return `${value / 16}rem`
}

function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  }
}

const createAppTheme = (mode: 'dark' | 'light') => {
  const isDark = mode === 'dark'

  const designTokens = {
    light: {
      // backgroundBody: '#e8ecf0',
      backgroundDefault: '#e8ecf0',
      backgroundPaper: '#FFFFFF',
      textPrimary: '#141414',
      textSecondary: '#00000099',
      textDisabled: alpha('#141414', 0.5),
      divider: '#E6ECF0',

      button: {
        primary: {
          bg: '#0078DB',
          hover: '#006AC2',
          active: '#005CA8',
          text: '#FFFFFF',
        },
        secondary: {
          bg: '#E0EFFB',
          hover: '#CCE0F0',
          active: '#BAD2E6',
          text: '#0078DB',
        },
        info: {
          bg: '#0288d1',
          hover: '#0278b7ff',
          active: '#018fdcff',
          text: '#FFFFFF',
        },
        success: {
          bg: '#4caf50',
          hover: '#3fad43ff',
          active: '#36a53aff',
          text: '#7DBBFF',
        },
        error: {
          bg: '#ef5350',
          hover: '#b8201d',
          active: '#db2a27',
          text: '#FFFFFF',
        },
        disabled: {
          bg: '#E6ECF0',
          text: '#7A858E',
        },
      },
    },

    dark: {
      // backgroundBody: '#111827',
      backgroundDefault: '#111827',
      backgroundPaper: '#1e263bff',
      textPrimary: '#F3F4F6',
      textSecondary: alpha('#F3F4F6', 0.9),
      textDisabled: alpha('#F3F4F6', 0.5),
      divider: alpha('#F3F4F6', 0.12),

      button: {
        primary: {
          bg: '#0359A3',
          hover: '#006AC2',
          active: '#005CA8',
          text: '#FFFFFF',
        },
        secondary: {
          bg: '#1C2733',
          hover: '#18222C',
          active: '#131C25',
          text: '#7DBBFF',
        },
        info: {
          bg: '#2b9edcff',
          hover: 'rgba(24, 37, 50, 1)',
          active: 'rgba(32, 47, 62, 1)',
          text: '#FFFFFF',
        },
        // main: '#0288d1',
        // light: '#03a9f4',
        // dark: '#01579b',
        success: {
          bg: '#129b67ff',
          hover: '#129b67ff',
          active: '#129b67ff',
          text: '#FFFFFF',
        },
        error: {
          bg: '#b80a0aff',
          hover: '#c31d1dff',
          active: '#c61313ff',
          text: '#FFFFFF',
        },
        disabled: {
          bg: '#3D3D3D',
          text: '#767676',
        },
      },
    },
  }

  const t = designTokens[mode]

  // paper: '#161d2dff', paper little lighter
  // #202332 apple
  // const darkTheme = {
  //   background: {
  //     default: '#001935', navi
  //     paper: '#001935',
  //   },
  // }

  return createTheme({
    typography: {
      fontFamily: '"Wix Madefor Text", "Arial", sans-serif',
      h1: {
        fontWeight: 'bold',
        letterSpacing: 0,
        fontFamily: 'Wix Madefor Display',
        lineHeight: 80 / 64,
        fontSize: pxToRem(40),
      },
      h2: {
        fontWeight: 'bold',
        letterSpacing: 0,
        fontFamily: 'Wix Madefor Display',
        lineHeight: 64 / 48,
        fontSize: pxToRem(32),
      },
      h3: {
        fontWeight: 'bold',
        letterSpacing: 0,
        fontFamily: 'Wix Madefor Display',
        lineHeight: 1.5,
        fontSize: pxToRem(24),
      },
      h4: {
        fontWeight: 'bold',
        letterSpacing: 0,
        fontFamily: 'Wix Madefor Display',
        lineHeight: 1.5,
        fontSize: pxToRem(20),
      },
      h5: {
        fontWeight: 'bold',
        letterSpacing: 0,
        fontFamily: 'Wix Madefor Display',
        lineHeight: 1.5,
        fontSize: pxToRem(18),
      },
      h6: {
        fontWeight: 'bold',
        letterSpacing: 0,
        fontFamily: 'Wix Madefor Display',
        lineHeight: 28 / 18,
        fontSize: pxToRem(17),
        ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
      },
      caption: {
        fontSize: pxToRem(12),
        fontWeight: 'bold',
        opacity: '0.64',
      },
      subtitle1: {
        lineHeight: 1.5,
        fontSize: pxToRem(16),
      },
      subtitle2: {
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
      },
      body1: {
        lineHeight: 1.5,
        fontSize: pxToRem(14),
      },
      body2: {
        lineHeight: 22 / 14,
        fontSize: pxToRem(11),
      },
    },
    palette: {
      mode,
      primary: {
        light: '#FFFFFF',
        main: '#0080FF',
        dark: '#0359A3',
      },
      secondary: {
        light: '#84A9FF',
        main: alpha('#0080FF', 0.12),
        dark: '#1939B7',
      },
      warning: {
        light: '#FFE16A',
        main: '#FFC107',
        dark: '#B78103',
        contrastText: '#212B36',
      },
      success: {
        main: '#2e7d32',
        light: '#4caf50',
        dark: '#1b5e20',
        contrastText: '#fff',
      },
      info: {
        main: '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
        contrastText: '#fff',
      },
      error: {
        main: '#d32f2f',
        light: '#ef5350',
        dark: '#c62828',
        contrastText: '#fff',
      },
      green: {
        100: '#DAF5EB',
        400: '#19C07F',
        500: '#10A86C',
      },
      red: {
        100: '#FFE2E4',
        400: '#FF4E5B',
        700: '#E52A38',
      },
      grey: {
        100: '#f5f5f5',
        200: '#f5f8fa',
        300: '#E6ECF0',
        400: '#D2DAE0',
        500: '#C5CDD3',
        600: '#929292',
        700: '#7A858E',
        800: '#141414',
        900: '#000',
      },
      blue: {
        100: '#F5F8FA',
        200: '#E0EFFB',
        300: '#CCE0F0',
        400: '#B4D7F4',
        500: '#0078DB',
        600: '#006AC2',
        700: '#005CA8',
        800: '#004B96',
      },
      cyan: {
        100: '#E4EEF5',
        500: '#3e98c7',
        600: '#0288d1',
      },
      brown: {
        50: '#e8ecf0',
        300: '#BEBEBE',
        500: '#555',
      },
      yellow: {
        50: '#FCF3DE',
        100: '#FFF2CC',
        200: '#FFE699',
        300: '#FFD966',
        400: '#FFC640',
        500: '#FFB31A',
        600: '#B78101',
      },
      black: {
        100: '#F5F5F5',
        200: '#E0E0E0',
        300: '#BDBDBD',
        400: '#9E9E9E',
        500: '#616161',
        600: '#424242',
        700: '#212121',
        800: '#0D0D0D',
      },
      background: {
        default: t.backgroundDefault,
        paper: t.backgroundPaper,
      },
      text: {
        primary: t.textPrimary,
        secondary: t.textSecondary,
        disabled: t.textDisabled,
      },
      divider: t.divider,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '#root': {
            backgroundColor: isDark ? t.backgroundDefault : t.backgroundPaper,
          },
          body: {
            backgroundColor: t.backgroundDefault,
          },
          main: {
            backgroundColor: t.backgroundDefault,
          },
          html: {
            backgroundColor: t.backgroundDefault,
          },
          // '#root': {
          //   backgroundColor: isDark ? darkTheme.background.default : '#F5F8FA',
          //   color: isDark ? '#F3F4F6' : '#141414',
          // },
          // main: {
          //   backgroundColor: `${lightTheme.background.default} !important`,
          // },
          // html: {
          //   backgroundColor: isDark ? darkTheme.background.default : '#F5F8FA',
          // },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          disablePortal: false,
          ListboxProps: {
            style: {
              maxHeight: 200,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            },
          },
          slotProps: {
            popper: {
              modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paperFullScreen: {
            paddingTop: 'var(--safe-top)',
            paddingBottom: 'var(--safe-bottom)',
            paddingLeft: 'var(--safe-left)',
            paddingRight: 'var(--safe-right)',
          },
          paper: {
            backgroundImage: 'none',
            '.close-button': {
              position: 'absolute',
              color: '#C5CDD3',
              top: `calc(8px + var(--safe-top))`,
              right: `calc(8px + var(--safe-right))`,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&.MuiButton-containedPrimary': {
              backgroundColor: t.button.primary.bg,
              color: t.button.primary.text,
              boxShadow: 'none',
              '&:hover': { backgroundColor: t.button.primary.hover },
              '&:active': { backgroundColor: t.button.primary.active },
            },
            '&.MuiButton-containedSecondary': {
              backgroundColor: t.button.secondary.bg,
              color: t.button.secondary.text,
              boxShadow: 'none',
              '&:hover': { backgroundColor: t.button.secondary.hover },
              '&:active': { backgroundColor: t.button.secondary.active },
            },
            '&.MuiButton-containedError': {
              backgroundColor: t.button.error.bg,
              color: t.button.error.text,
              boxShadow: 'none',
              '&:hover': { backgroundColor: t.button.error.hover },
              '&:active': { backgroundColor: t.button.error.active },
            },
            '&.MuiButton-containedSuccess': {
              backgroundColor: t.button.success.bg,
              color: t.button.success.text,
              boxShadow: 'none',
              '&:hover': { backgroundColor: t.button.success.hover },
              '&:active': { backgroundColor: t.button.success.active },
            },
            '&.MuiButton-containedInfo': {
              backgroundColor: t.button.info.bg,
              color: t.button.info.text,
              boxShadow: 'none',
              '&:hover': { backgroundColor: t.button.info.hover },
              '&:active': { backgroundColor: t.button.info.active },
            },
            '&:disabled': {
              backgroundColor: t.button.disabled.bg,
              color: t.button.disabled.text,
              cursor: 'not-allowed',
            },
          },
        },
      },
      // MuiAlert: {
      //   styleOverrides: {
      //     root: {
      //       '&.MuiAlert-colorError': {
      //         backgroundColor: isDark ? '' : '',
      //       },
      //     },
      //   },
      // },
      // MuiBottomNavigation: {
      //   styleOverrides: {
      //     root: {
      //       height: '45px',
      //       backgroundColor: isDark ? '#1F2937' : '#FFF',
      //     },
      //   },
      // },
      // MuiBottomNavigationAction: {
      //   styleOverrides: {
      //     root: {
      //       '@media all and (display-mode: standalone)': {
      //         paddingBottom: '15px',
      //       },
      //       minWidth: '40px',
      //       color: isDark ? '#E5E7EB' : '#666666',
      //       '&.Mui-selected': {
      //         color: isDark ? '#60A5FA' : '#006AC2',
      //       },
      //     },
      //   },
      // },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            '.MuiTablePagination-selectLabel': {
              fontSize: '14px !important',
            },
            '.MuiTablePagination-displayedRows': {
              fontSize: '14px !important',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.divider}`,
          },
          head: {
            fontWeight: 'bold',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }) => ({
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
              {
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                WebkitBoxShadow: '0 0 0 100px transparent inset !important',
                transition: 'background-color 5000s ease-in-out 0s',
                caretColor: theme.palette.text.primary,
              },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: 16,
            fontWeight: 900,
            textTransform: 'uppercase',
            color: '#FFFFFF',
            backgroundColor: theme.palette.primary.main,
          }),
        },
      },
    },
  })
}

export const StyledContainer: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { resolvedMode } = useThemeMode()

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container component="div" maxWidth={false} disableGutters sx={{ height: '100%' }}>
        {children}
      </Container>
    </ThemeProvider>
  )
}
